import { ThemeProvider } from "@mui/material";
import { getTheme } from "@/style/theme.ts";
import { NuqsAdapter } from "nuqs/adapters/tanstack-router";
import Rollbar from "rollbar";
import { ErrorBoundary, Provider as RollbarProvider } from "@rollbar/react";
import { HelmetProvider } from "react-helmet-async";
import AutoThemeSwitcher from "@/hoc/AutoThemeSwitcher.tsx";
import { getBrowserCompatibility } from "@/services/support.ts";

// Initialize Rollbar instance and expose on window for global access
const browserCompatibility = getBrowserCompatibility();
const hasRollbarToken = !!import.meta.env.VITE_ROLLBAR_TOKEN;

const UPLOAD_TOKEN_PATH_RE = /(\/candidature\/documents\/)[^/?#]+/g;

const rollbarInstance = new Rollbar({
  enabled:
    import.meta.env.PROD &&
    hasRollbarToken &&
    browserCompatibility.isCompatible,
  accessToken: (import.meta.env.VITE_ROLLBAR_TOKEN as string) || "disabled",
  captureUncaught: true,
  captureUnhandledRejections: true,
  environment: import.meta.env.MODE,
  // Champs PII à masquer avant envoi à Rollbar. Aligné sur les entités
  // Application / Contact / ApplicationNote et sur le token d'upload public.
  scrubFields: [
    "uploadToken",
    "upload_token",
    "email",
    "phone",
    "firstName",
    "lastName",
    "fullName",
    "message",
    "note",
  ],
  scrubTelemetryInputs: true,
  transform: (payload) => {
    const request = (payload as { request?: { url?: string } }).request;
    if (request?.url) {
      request.url = request.url.replace(UPLOAD_TOKEN_PATH_RE, "$1[redacted]");
    }
    const body = (
      payload as {
        body?: { trace?: { frames?: { filename?: string }[] } };
      }
    ).body;
    if (body?.trace?.frames) {
      for (const frame of body.trace.frames) {
        if (frame.filename) {
          frame.filename = frame.filename.replace(
            UPLOAD_TOKEN_PATH_RE,
            "$1[redacted]"
          );
        }
      }
    }
  },
  payload: {
    client: {
      javascript: {
        code_version: "1.0.0",
        source_map_enabled: true,
      },
    },
  },
});

// Expose Rollbar on window for ErrorBoundary and ErrorMessage components
if (typeof window !== "undefined") {
  window.Rollbar = rollbarInstance;
}

type ProvidersProps = {
  children: React.ReactNode;
};

const Providers = ({ children }: ProvidersProps) => {
  const theme = getTheme();

  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <AutoThemeSwitcher>
          <RollbarProvider instance={rollbarInstance}>
            <ErrorBoundary>
              <NuqsAdapter>{children}</NuqsAdapter>
            </ErrorBoundary>
          </RollbarProvider>
        </AutoThemeSwitcher>
      </ThemeProvider>
    </HelmetProvider>
  );
};

export default Providers;
